import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, query, where, getDocs, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getDailyWord } from '../data/wordList';
import { ArrowLeft, Save, Lightbulb, Trash2, Share2 } from 'lucide-react';

export default function DailyWordExercise() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const word = getDailyWord();

  const todayET = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

  const [loadingCheck, setLoadingCheck] = useState(true);
  const [todaySubmission, setTodaySubmission] = useState<{ id: string; content: string; word: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [shareConfirmOpen, setShareConfirmOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);

  const [content, setContent] = useState('');
  const [saveMode, setSaveMode] = useState<'save' | 'share' | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    const check = async () => {
      setLoadingCheck(true);
      try {
        const q = query(
          collection(db, 'exerciseSaves'),
          where('userId', '==', user.uid),
          where('date', '==', todayET)
        );
        const snap = await getDocs(q);
        const existing = snap.docs.find(d => d.data().type === 'daily-word');
        setTodaySubmission(existing ? { id: existing.id, content: existing.data().content, word: existing.data().word } : null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingCheck(false);
      }
    };
    check();
  }, [user]);

  const sharePost = async (shareContent: string) => {
    const userDoc = await getDoc(doc(db, 'users', user!.uid));
    const userData = userDoc.exists() ? userDoc.data() : null;
    const username = userData?.username || 'Anonymous';

    await addDoc(collection(db, 'posts'), {
      content: shareContent,
      authorId: user!.uid,
      authorName: username,
      authorPhoto: user!.photoURL || '',
      createdAt: new Date(),
      likes: 0,
      comments: 0,
      shares: 0,
      likedBy: [],
      tags: ['daily-word']
    });
  };

  const handleSave = async (shareAfter: boolean) => {
    if (!user || !content.trim()) return;
    setSaveMode(shareAfter ? 'share' : 'save');
    setError('');
    const trimmedContent = content.trim();
    try {
      await addDoc(collection(db, 'exerciseSaves'), {
        userId: user.uid,
        type: 'daily-word',
        word,
        content: trimmedContent,
        date: todayET,
        createdAt: serverTimestamp(),
      });

      if (shareAfter) {
        try {
          await sharePost(`Daily Word Exercise: ${word}\n\n${trimmedContent}`);
        } catch (shareErr) {
          console.error(shareErr);
          setError('Saved, but failed to share to feed.');
        }
      }

      setSaved(true);
      setTimeout(() => {
        navigate(shareAfter ? '/community' : '/exercises');
        window.scrollTo(0, 0);
      }, 1200);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Try again.');
      setSaveMode(null);
    }
  };

  const handleShare = async () => {
    if (!user || !todaySubmission) return;
    setSharing(true);
    try {
      await sharePost(`Daily Word Exercise: ${todaySubmission.word}\n\n${todaySubmission.content}`);
      setShareConfirmOpen(false);
      setShared(true);
    } catch (err) {
      console.error(err);
      setError('Failed to share. Try again.');
    } finally {
      setSharing(false);
    }
  };

  const handleDelete = async () => {
    if (!todaySubmission) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'exerciseSaves', todaySubmission.id));
      setTodaySubmission(null);
    } catch (err) {
      console.error(err);
      setDeleting(false);
    }
  };

  const Header = (
    <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl p-8 text-white mb-8 shadow-lg relative overflow-hidden">
      <div className="absolute -right-4 -top-4 opacity-10">
        <Lightbulb size={160} />
      </div>
      <p className="text-orange-100 text-xs font-bold uppercase tracking-widest mb-3 relative z-10">
        Today's Word Exercise
      </p>
      <h1 className="text-2xl font-extrabold leading-snug relative z-10 mb-1">
        Write a joke using the word
      </h1>
      <p className="text-5xl font-black tracking-tight capitalize relative z-10 mt-2">
        {word}
      </p>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 min-h-screen flex flex-col">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-orange-500 transition-colors mb-8 font-medium text-sm w-fit"
      >
        <ArrowLeft size={16} /> Back
      </button>

      {Header}

      {loadingCheck ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Checking…</div>
      ) : todaySubmission ? (
        /* Already submitted today */
        <div className="flex-1 flex flex-col gap-4">
          <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-2xl px-5 py-4">
            <p className="text-amber-700 dark:text-amber-400 text-sm font-semibold">
              You've already submitted today's exercise. Delete it below to start over.
            </p>
          </div>
          <div className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-700 rounded-2xl p-5 shadow-sm">
            <p className="text-gray-700 dark:text-gray-300 text-[15px] leading-relaxed whitespace-pre-wrap">{todaySubmission.content}</p>
          </div>
          <div className="flex flex-wrap justify-end gap-3">
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center gap-2 text-sm font-semibold text-red-500 hover:text-red-600 border border-red-200 hover:border-red-400 px-5 py-2.5 rounded-full transition-all disabled:opacity-50"
            >
              <Trash2 size={15} />
              {deleting ? 'Deleting…' : 'Delete & start over'}
            </button>
            <button
              onClick={() => setShareConfirmOpen(true)}
              disabled={shared}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all shadow-md active:scale-95"
            >
              <Share2 size={15} />
              {shared ? 'Shared!' : 'Share to Feed'}
            </button>
          </div>
        </div>
      ) : (
        /* Fresh form */
        <div className="flex-1 flex flex-col gap-4">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={`Start writing your joke using "${word}"...`}
            className="flex-1 w-full min-h-[280px] bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-700 rounded-2xl p-5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-[16px] leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 shadow-sm font-medium"
            autoFocus
          />
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-gray-400 font-medium">{content.length} characters</span>
              {error && <span className="text-xs text-red-500 font-medium">{error}</span>}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <button
                onClick={() => handleSave(false)}
                disabled={!content.trim() || !!saveMode || saved}
                className="flex items-center gap-2 bg-orange-100 dark:bg-orange-500/10 hover:bg-orange-200 dark:hover:bg-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed text-orange-700 dark:text-orange-400 font-bold px-5 py-3 rounded-full transition-all active:scale-95"
              >
                <Save size={18} />
                {saved && saveMode === 'save' ? 'Saved! Redirecting…' : saveMode === 'save' ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={!content.trim() || !!saveMode || saved}
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-6 py-3 rounded-full transition-all shadow-md active:scale-95"
              >
                <Share2 size={18} />
                {saved && saveMode === 'share' ? 'Saved & Shared! Redirecting…' : saveMode === 'share' ? 'Sharing…' : 'Save & Share to Feed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Confirmation Modal */}
      {shareConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-navy-800 p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Share to Feed</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to share today's exercise to your feed? This will create a new post that everyone can see.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShareConfirmOpen(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white bg-gray-100 dark:bg-navy-700 hover:bg-gray-200 dark:hover:bg-navy-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleShare}
                disabled={sharing}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                {sharing ? 'Sharing…' : 'Share'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
